/* Super Admin > Feedback — review, filter, triage feedback submissions.
   Mounted at /app/feedback. Cookie-authenticated (same session as the admin app). */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Mail, MailWarning, MailCheck, MessageSquareText } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const STATUSES = ["New", "Under Review", "Resolved", "Closed"];
const CATEGORIES = ["General Feedback", "Usability", "Diagnostic Questions", "Portfolio Manager Portal",
  "School User Portal", "Sales Portal", "Super Admin", "Reporting", "Performance", "Bug / Error", "Feature Request", "Other"];
const PORTALS = ["Super Admin", "Sales Portal", "Portfolio Manager", "School User", "Other"];

const STATUS_CLR = {
  New: "bg-blue-100 text-blue-700", "Under Review": "bg-amber-100 text-amber-700",
  Resolved: "bg-emerald-100 text-emerald-700", Closed: "bg-slate-200 text-slate-600",
};
const EMAIL_ICON = {
  sent: <MailCheck className="h-4 w-4 text-emerald-600" />,
  mocked: <Mail className="h-4 w-4 text-slate-400" />,
  failed: <MailWarning className="h-4 w-4 text-red-500" />,
  pending: <Mail className="h-4 w-4 text-slate-400" />,
};

const fmtDate = (v) => { try { return new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return "—"; } };

const FeedbackAdminPage = () => {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [filters, setFilters] = useState({ status: "all", portal_type: "all", category: "all", days: "all" });
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    for (const [k, v] of Object.entries(filters)) if (v && v !== "all") params[k] = v;
    try {
      const r = await axios.get(`${API}/api/admin/feedback`, { ...cfg, params });
      setRows(r.data.feedback || []);
      setCounts(r.data.counts || {});
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load feedback"); setRows([]); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (r) => { setSelected(r); setNotes(r.resolution_notes || ""); };

  const setStatus = async (status) => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/api/admin/feedback/${selected.id}`, { status, resolution_notes: notes }, cfg);
      toast.success(`Marked ${status}`);
      setSelected({ ...selected, status, resolution_notes: notes });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/api/admin/feedback/${selected.id}`, { resolution_notes: notes }, cfg);
      toast.success("Notes saved"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const retryEmail = async (r) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/admin/feedback/${r.id}/retry-email`, {}, cfg);
      toast.success(`Email ${res.data.email_status}`);
      load();
      if (selected?.id === r.id) setSelected({ ...selected, email_status: res.data.email_status });
    } catch (err) { toast.error(err?.response?.data?.detail || "Retry failed"); }
    finally { setBusy(false); }
  };

  const Filter = ({ k, label, options }) => (
    <Select value={filters[k]} onValueChange={(v) => setFilters((f) => ({ ...f, [k]: v }))}>
      <SelectTrigger className="w-full sm:w-[170px]" data-testid={`feedback-filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}</SelectItem>
        {options.map((o) => <SelectItem key={o.v ?? o} value={String(o.v ?? o)}>{o.l ?? o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1500px] mx-auto" data-testid="feedback-admin-page">
      <div className="flex items-center gap-3 mb-1">
        <MessageSquareText className="h-6 w-6 text-[#007a9e]" />
        <h1 className="text-2xl font-bold text-[#111827]">Feedback</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">User feedback submitted from every portal. Emailed to dirk@589labs.ai and stored here for review.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {STATUSES.map((s) => (
          <div key={s} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`feedback-count-${s.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="text-[11.5px] uppercase tracking-wide text-slate-400">{s}</div>
            <div className="text-2xl font-bold text-slate-800">{counts[s] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Filter k="status" label="All Statuses" options={STATUSES} />
        <Filter k="portal_type" label="All Portals" options={PORTALS} />
        <Filter k="category" label="All Categories" options={CATEGORIES} />
        <Filter k="days" label="Any Time" options={[{ v: 7, l: "Last 7 days" }, { v: 30, l: "Last 30 days" }, { v: 90, l: "Last 90 days" }]} />
        <Button variant="outline" size="sm" onClick={load} data-testid="feedback-refresh"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {rows === null ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
            : rows.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No feedback matches these filters.</div>
              : (
                <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                  {rows.map((r) => (
                    <button key={r.id} onClick={() => openDetail(r)} data-testid={`feedback-row-${r.id}`}
                      className={`w-full text-left p-4 hover:bg-slate-50 ${selected?.id === r.id ? "bg-slate-50" : ""}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-slate-800 truncate">{r.user_name_snapshot}</span>
                        <span className={`text-[11.5px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_CLR[r.status] || "bg-slate-100"}`}>{r.status}</span>
                      </div>
                      <div className="text-[12px] text-slate-500 mt-0.5">{r.portal_type} · {r.feedback_category}</div>
                      <div className="text-[13px] text-slate-600 mt-1 line-clamp-2">{r.feedback_text}</div>
                      <div className="flex items-center gap-2 mt-1.5 text-[11.5px] text-slate-400">
                        {EMAIL_ICON[r.email_status] || EMAIL_ICON.pending}
                        <span>{fmtDate(r.submitted_at)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="feedback-detail">
          {!selected ? <div className="text-sm text-slate-400 text-center py-16">Select a submission to review it.</div> : (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-bold text-slate-800">{selected.user_name_snapshot}</h3>
                  <span className={`text-[11.5px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_CLR[selected.status]}`}>{selected.status}</span>
                </div>
                <div className="text-[12px] text-slate-500">{selected.user_email_snapshot} · {selected.user_role_snapshot}</div>
              </div>
              <dl className="grid grid-cols-2 gap-y-1.5 text-[12px]">
                <dt className="text-slate-400">Portal</dt><dd className="text-slate-700 text-right">{selected.portal_type}</dd>
                <dt className="text-slate-400">Category</dt><dd className="text-slate-700 text-right">{selected.feedback_category}</dd>
                <dt className="text-slate-400">Page</dt><dd className="text-slate-700 text-right truncate">{selected.page_feature || selected.current_route || "—"}</dd>
                <dt className="text-slate-400">Contact OK?</dt><dd className="text-slate-700 text-right">{selected.contact_permission ? "Yes" : "No"}</dd>
                <dt className="text-slate-400">Submitted</dt><dd className="text-slate-700 text-right">{fmtDate(selected.submitted_at)}</dd>
                <dt className="text-slate-400">Email</dt><dd className="text-slate-700 text-right flex items-center justify-end gap-1">{EMAIL_ICON[selected.email_status]}<span className="capitalize">{selected.email_status}</span></dd>
              </dl>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-[11.5px] uppercase tracking-wide text-slate-400 mb-1">Feedback</div>
                <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{selected.feedback_text}</div>
              </div>
              {selected.attachment_url && (
                <a href={`${API}${selected.attachment_url}`} target="_blank" rel="noreferrer" className="text-[13px] text-blue-600 underline" data-testid="feedback-attachment-link">View attached screenshot</a>
              )}
              {selected.email_status === "failed" && (
                <Button variant="outline" size="sm" onClick={() => retryEmail(selected)} disabled={busy} data-testid="feedback-retry-email">
                  <RefreshCw className="h-4 w-4 mr-1" /> Retry notification email
                </Button>
              )}
              <div>
                <label className="text-[12px] font-medium text-slate-600">Resolution notes</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 resize-none" data-testid="feedback-notes" />
                <Button variant="ghost" size="sm" onClick={saveNotes} disabled={busy} className="mt-1" data-testid="feedback-save-notes">Save notes</Button>
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                <Button size="sm" variant="outline" onClick={() => setStatus("Under Review")} disabled={busy} data-testid="feedback-mark-review">Under Review</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setStatus("Resolved")} disabled={busy} data-testid="feedback-mark-resolved">Resolved</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus("Closed")} disabled={busy} data-testid="feedback-mark-closed">Closed</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackAdminPage;
